import { Link } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout.js';

const STEPS = [
  {
    title: 'Get access',
    text: 'Velinko is invite-only. Request access with your email; once it is approved you can sign in.',
  },
  {
    title: 'Install and pair',
    text: 'Install the Android app, choose Pair device on the dashboard and type the 6-digit code into the app.',
  },
  {
    title: 'Read codes in the browser',
    text: 'New SMS reach the dashboard within seconds. One-time codes are highlighted so you can copy them in one click.',
  },
];

const FEATURES = [
  {
    icon: '⚡',
    title: 'OTP codes first',
    text: 'Codes are pulled out of each message and shown as a chip you can copy, no scrolling through text.',
  },
  {
    icon: '💬',
    title: 'Full SMS history',
    text: 'Search conversations, archive old threads and reply from the browser through your phone.',
  },
  {
    icon: '🔋',
    title: 'Battery friendly',
    text: 'The phone wakes only when a message arrives or the dashboard asks. Built and tested for Samsung One UI.',
  },
  {
    icon: '🩺',
    title: 'Health at a glance',
    text: 'See battery, network, queue size and last sync for every phone, and run a diagnostic when something looks off.',
  },
  {
    icon: '🔒',
    title: 'Private by design',
    text: 'No ads, no tracking. Export or delete everything at any time from your account page.',
  },
  {
    icon: '🛡',
    title: 'Moderated accounts',
    text: 'Only approved emails can sign up, sign in or pair a phone, and access can be revoked instantly.',
  },
];

function PreviewCard(): JSX.Element {
  return (
    <div className="preview" aria-hidden="true">
      <div className="preview-bar">
        <span />
        <span />
        <span />
      </div>
      <div className="preview-body">
        <div className="preview-side">
          <div className="preview-device">
            <strong>Galaxy A22</strong>
            <small>
              <i className="dot dot-ok" /> Online · synced now
            </small>
          </div>
          <div className="preview-line w70" />
          <div className="preview-line w50" />
        </div>
        <div className="preview-main">
          {[
            ['Bank', 'Your login code is 482913', '482913', 'now'],
            ['Shop', 'Verification code 771204. Never share it.', '771204', '2 min'],
            ['Delivery', 'Your parcel is ready for pickup', null, '1 h'],
          ].map(([from, text, otp, time]) => (
            <div className="preview-row" key={from}>
              <div className="preview-avatar">{(from ?? '?').charAt(0)}</div>
              <div className="preview-text">
                <div className="preview-from">
                  {from}
                  <small>{time}</small>
                </div>
                <div className="preview-msg">{text}</div>
              </div>
              {otp ? <span className="otp-chip">{otp}</span> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HomePage(): JSX.Element {
  const signedIn = !!localStorage.getItem('velinko_token');
  return (
    <PublicLayout wide>
      <section className="hero">
        <div className="site-inner hero-grid">
          <div>
            <span className="eyebrow">Android → Browser SMS sync</span>
            <h1>Your phone&apos;s SMS and OTP codes, right in your browser.</h1>
            <p className="lead">
              Velinko forwards text messages from your Android phone to a secure web dashboard, so
              the login code you are waiting for is on the screen you are already looking at.
            </p>
            <div className="hero-cta">
              {signedIn ? (
                <Link to="/dashboard" className="btn btn-primary btn-lg">
                  Open dashboard
                </Link>
              ) : (
                <>
                  <Link to="/register" className="btn btn-primary btn-lg">
                    Request access
                  </Link>
                  <Link to="/login" className="btn btn-lg">
                    Sign in
                  </Link>
                </>
              )}
              <Link to="/docs" className="btn btn-ghost btn-lg">
                Read the docs →
              </Link>
            </div>
            <p className="hero-note">
              Invite-only during the beta. Android 13+ · tested on Samsung Galaxy.
            </p>
          </div>
          <PreviewCard />
        </div>
      </section>

      <section className="site-section">
        <div className="site-inner">
          <h2 className="section-title">How it works</h2>
          <ol className="steps">
            {STEPS.map((s, i) => (
              <li key={s.title} className="step-card">
                <span className="step-num">{i + 1}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="site-section">
        <div className="site-inner">
          <h2 className="section-title">What you get</h2>
          <div className="feature-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="feature-card">
                <span className="feature-icon" aria-hidden="true">
                  {f.icon}
                </span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="site-section">
        <div className="site-inner">
          <div className="cta-band">
            <div>
              <h2>New here?</h2>
              <p>
                The setup guide walks through installing the app, pairing and the Samsung battery
                settings that keep sync reliable.
              </p>
            </div>
            <Link to="/docs#getting-started" className="btn btn-primary btn-lg">
              Start the setup guide
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
